/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import IndexApp from "../../Components/IndexApp";
import { formatRelativeDate, useIsClient } from "../../Components/AppBar/NotificationList";
import useNotifications from "../../Hooks/useNotifications";
import { NotificationItem } from "../../Repositories/NotificationRepository";
import { NotificationDestination, resolveNotificationRoute } from "../../config/NOTIFICATION_ROUTES";
import { groupNotifications, notificationCategoryLabel } from "../../Utils/notificationGroups";
import { DV2 } from "../DeclarationV2/theme";
import { plural } from "../DeclarationV2/format";
import { vocab } from "../DeclarationV2/vocabulary";
import { Button, Card, TextLink, useDv2Theme } from "../DeclarationV2/layout/Ui";

/** Une notification « à traiter » : priorité haute et pas encore lue. */
const needsAction = (item: NotificationItem): boolean => item.priority === "high" && !item.isRead;

/** Couleur du point : rouge à traiter, orange à surveiller, vert bonne nouvelle. */
const dotColor = (item: NotificationItem): string =>
    item.priority === "high" ? DV2.danger : item.priority === "low" ? DV2.success : DV2.warning;

/** Libellé de l'action sous le message, selon la destination de la notification. */
const actionLabel = (item: NotificationItem): string | null => {
    switch (item.target?.route) {
        case "client_detail": return `Voir la fiche ${vocab.client()}`;
        case "client_list": return `Voir mes ${vocab.clients()}`;
        case "courses": return `Voir mes ${vocab.courses()}`;
        case "documents": return "Voir mes documents";
        case "travel_subscription": return "Déposer un abonnement";
        case "settings": return "Ouvrir les paramètres";
        default: return null;
    }
};

type QuickFilter = "all" | "unread" | "action";

/**
 * Page « Notifications » repensée (maquette 10a) : sous-titre qui compte ce
 * qui reste à faire, « Tout marquer comme lu », filtres Toutes / Non lues /
 * À traiter puis par catégorie, les notifications à traiter en tête, les
 * autres groupées par ancienneté, chaque ligne avec son message et son
 * action. Même store `useNotifications` que la cloche ; l'ancienne page
 * reste dans le code et sert toujours aux clients.
 */
const NotificationsV2Page = () => {
    const { primary, fontFamily, text } = useDv2Theme();
    const navigate = useNavigate();
    const isClient = useIsClient();
    const currentUser = useSelector((state: any) => state?.user?.currentUser);
    const account = currentUser?.isClient ? currentUser?.client : currentUser?.teachr;
    const notifications = useNotifications();
    const { enabled, items, total, unreadCount, categories, filters, loaded, loading, loadingMore, hasMore, refresh, loadMore, markAsRead, markAllAsRead, hide, setFilters } = notifications;
    const [quick, setQuick] = useState<QuickFilter>(filters.unreadOnly ? "unread" : "all");

    useEffect(() => {
        if (enabled) refresh();
    }, [enabled]);

    const selectQuick = (next: QuickFilter) => {
        setQuick(next);
        setFilters({ unreadOnly: next === "unread" });
    };

    const visible = useMemo(() => (quick === "action" ? items.filter(needsAction) : items), [items, quick]);
    const actionItems = useMemo(() => visible.filter(needsAction), [visible]);
    const others = useMemo(() => (quick === "action" ? [] : visible.filter((item) => !needsAction(item))), [visible, quick]);
    const groups = useMemo(() => groupNotifications(others), [others]);
    const actionCount = items.filter(needsAction).length;
    const hasUnread = unreadCount > 0 || items.some((item) => !item.isRead);

    const open = (item: NotificationItem, destination: NotificationDestination | null) => {
        markAsRead(item);
        if (!destination) return;
        navigate(destination.path, destination.state ? { state: destination.state } : undefined);
    };

    const subtitle = !loaded
        ? "Chargement de vos notifications…"
        : unreadCount === 0
            ? "Vous êtes à jour, aucune notification non lue."
            : `${plural(unreadCount, "non lue")}${actionCount > 0 ? `, ${actionCount} ${actionCount > 1 ? "demandent" : "demande"} une action de votre part` : ""}`;

    const pill = (active: boolean): React.CSSProperties => ({
        fontFamily, fontSize: 13, fontWeight: 700, padding: "8px 14px", borderRadius: 999, border: "none", cursor: "pointer",
        background: active ? primary : DV2.tint, color: active ? "#fff" : primary, whiteSpace: "nowrap",
    });

    const row = (item: NotificationItem, last: boolean) => {
        const destination = resolveNotificationRoute(item.target, isClient);
        const label = destination ? actionLabel(item) : null;
        return (
            <div key={item.id} className="group flex items-start gap-3" style={{ padding: "14px 0", borderBottom: last ? "none" : `1px solid ${DV2.borderSoft}`, background: item.isRead ? "transparent" : undefined }}>
                <span aria-hidden style={{ width: 10, height: 10, borderRadius: "50%", background: dotColor(item), marginTop: 6, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                        <button
                            type="button"
                            onClick={() => open(item, destination)}
                            className="text-left"
                            style={{ background: "none", border: "none", padding: 0, fontFamily, fontSize: 15, fontWeight: item.isRead ? 700 : 800, color: text, lineHeight: 1.4, cursor: destination ? "pointer" : "default" }}
                        >
                            {!item.isRead && <span className="sr-only">Non lue : </span>}
                            {item.title}
                        </button>
                        <span style={{ fontSize: 12, color: DV2.placeholder, whiteSpace: "nowrap" }}>{formatRelativeDate(item.createdAt)}</span>
                    </div>
                    {item.body && <p style={{ fontSize: 14, color: DV2.textSoft, lineHeight: 1.5, margin: "4px 0 0" }}>{item.body}</p>}
                    <div className="flex flex-wrap items-center gap-4" style={{ marginTop: 8 }}>
                        {label && <TextLink label={label} onClick={() => open(item, destination)} />}
                        {!item.isRead && <TextLink label="Marquer comme lue" size={12} onClick={() => markAsRead(item)} />}
                        <button type="button" onClick={() => hide(item)} className="opacity-0 group-hover:opacity-100 focus:opacity-100" style={{ background: "none", border: "none", padding: 0, fontFamily, fontSize: 12, color: DV2.placeholder, cursor: "pointer", transition: "opacity .12s" }}>Masquer</button>
                    </div>
                </div>
                {!item.isRead && <span aria-hidden style={{ width: 8, height: 8, borderRadius: "50%", background: primary, marginTop: 8, flexShrink: 0 }} />}
            </div>
        );
    };

    const section = (title: string, list: NotificationItem[]) => (
        <section key={title} aria-label={title}>
            <h2 style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: DV2.placeholder, margin: "18px 0 2px" }}>{title}</h2>
            {list.map((item, i) => row(item, i === list.length - 1))}
        </section>
    );

    const body = () => {
        if (!loaded) {
            return (
                <div style={{ padding: "32px 0", textAlign: "center", color: DV2.muted, fontSize: 14 }}>
                    {loading ? <><CircularProgress size={18} style={{ marginRight: 8, verticalAlign: "middle" }} /> Chargement…</> : "Vos notifications ne peuvent pas être affichées pour le moment. Réessayez un peu plus tard."}
                </div>
            );
        }
        if (visible.length === 0) {
            return (
                <div style={{ padding: "32px 0", textAlign: "center", color: DV2.muted, fontSize: 14 }}>
                    {quick !== "all" || filters.category ? "Aucune notification pour ce filtre." : "Aucune notification. Les événements importants de votre compte s'afficheront ici."}
                </div>
            );
        }
        return (
            <>
                {actionItems.length > 0 && section("À traiter", actionItems)}
                {groups.map((group) => section(group.label, group.items))}
                <div className="flex items-center justify-center" style={{ paddingTop: 18, fontSize: 13, color: DV2.placeholder }}>
                    {hasMore
                        ? <Button label="Voir plus" variant="secondary" size="sm" onClick={loadMore} loading={loadingMore} />
                        : "Vous avez tout vu."}
                </div>
            </>
        );
    };

    return (
        <IndexApp
            navBar={{ index: 0 }}
            teachr={account}
            component={
                <main className="px-4" style={{ background: DV2.pageBg, minHeight: "calc(100vh - 64px)", fontFamily, color: text }}>
                    <div className="mx-auto w-full /max-w-4xl px-4 sm:px-6 py-4 lg:py-8 flex flex-col gap-5">
                        <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                            <div>
                                <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0, lineHeight: 1.2 }}>Notifications</h1>
                                <div style={{ fontSize: 14, color: DV2.muted, marginTop: 4 }}>{subtitle}</div>
                            </div>
                            <Button label="Tout marquer comme lu" variant="secondary" onClick={markAllAsRead} disabled={!hasUnread} />
                        </header>

                        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filtrer les notifications">
                            <button type="button" aria-pressed={quick === "all"} style={pill(quick === "all")} onClick={() => selectQuick("all")}>Toutes{loaded && !filters.category ? `, ${total}` : ""}</button>
                            <button type="button" aria-pressed={quick === "unread"} style={pill(quick === "unread")} onClick={() => selectQuick("unread")}>Non lues{loaded ? `, ${unreadCount}` : ""}</button>
                            <button type="button" aria-pressed={quick === "action"} style={pill(quick === "action")} onClick={() => selectQuick("action")}>À traiter{loaded ? `, ${actionCount}` : ""}</button>
                            {categories.length > 0 && <span aria-hidden style={{ width: 1, height: 22, background: DV2.border, margin: "0 4px" }} />}
                            {categories.map((category) => {
                                const active = filters.category === category;
                                return (
                                    <button key={category} type="button" aria-pressed={active} style={pill(active)} onClick={() => setFilters({ category: active ? null : category })}>
                                        {notificationCategoryLabel(category)}
                                    </button>
                                );
                            })}
                        </div>

                        <Card padding={22}>{body()}</Card>
                    </div>
                </main>
            }
        />
    );
};

export default NotificationsV2Page;
